import { useRecoilState } from 'recoil'
import {
  ChangeEvent,
  DragEvent,
  ClipboardEvent,
  useRef,
  useState,
  useCallback,
} from 'react'
import { createStateFactory } from 'Store/create'
import { CreateDeleteIcon } from 'Icons'
import { PlusAddIcon } from 'Comps/svg/PlusAddIcon'
import { usePathname, useSearchParams } from 'next/navigation'
import clsx from 'clsx'
import { message } from 'antd'
import { MAX_USER_UPLOAD_IMG_SIZE } from '@/constant/img'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'

type UploadCharImgProps = {
  disabled: boolean
  scene?: string
}

export const GenerateUploadCharImg = ({
  disabled,
  scene = 'character_image',
}: UploadCharImgProps) => {
  const { t, lang } = useI18n()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isUpdateChar =
    pathname === withLocale('/update', lang) ||
    searchParams.get('update') === 'true'
  const [createCharForm, setCreateCharForm] = useRecoilState(
    createStateFactory(false, scene)
  )
  const [updateCharForm, setUpdateCharForm] = useRecoilState(
    createStateFactory(true, scene)
  )
  const charForm = isUpdateChar ? updateCharForm : createCharForm
  const setCharForm = isUpdateChar ? setUpdateCharForm : setCreateCharForm
  const {
    generateImgReference: selectedFile,
    generateImgReferencePreviewUrl: previewUrl,
    imgPrompt,
  } = charForm

  const setImgReference = (file: File | null) => {
    setCharForm((prev) => ({ ...prev, generateImgReference: file }))
  }
  const setImgReferencePreviewUrl = (url: string | null) => {
    setCharForm((prev) => ({
      ...prev,
      generateImgReferencePreviewUrl: url,
    }))
  }
  const setImgReferencePath = useCallback(
    (path: string | null) => {
      setCharForm((prev) => ({
        ...prev,
        generateImgReferencePath: path,
      }))
    },
    [setCharForm]
  )
  const setImgReferenceUrl = useCallback(
    (url: string | null) => {
      setCharForm((prev) => ({
        ...prev,
        generateImgReferenceUrl: url,
      }))
    },
    [setCharForm]
  )

  const handleSaveOriginImg = async (userFile: File) => {
    if (userFile.size > MAX_USER_UPLOAD_IMG_SIZE) {
      message.error(
        t('The uploaded image exceeds the maximum allowed size of 20MB.')
      )
      return
    }
    setImgReference(userFile)
    setImgReferencePreviewUrl(URL.createObjectURL(userFile))
    setImgReferencePath(null)
    setImgReferenceUrl(null)
  }
  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files.length) return
    const userFile: File = e.target.files[0]
    handleSaveOriginImg(userFile)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleSaveOriginImg(e.dataTransfer.files[0])
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (e.clipboardData.files.length) {
      handleSaveOriginImg(e.clipboardData.files[0])
    }
  }

  const handleClick = () => {
    if (selectedFile) return
    fileInputRef.current?.click()
  }

  const handleCancel = () => {
    setImgReference(null)
    setImgReferencePreviewUrl(null)
    setImgReferencePath(null)
    setImgReferenceUrl(null) // 清除之前上传的图片URL
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setCharForm((prev) => {
      return {
        ...prev,
        isMimicStyle: false,
        isMimicFace: false,
      }
    })
  }

  if (!selectedFile)
    return (
      <div
        onDrop={handleDrop}
        onPaste={handlePaste}
        onClick={handleClick}
        className={clsx(
          'flex h-full w-full cursor-pointer flex-col items-center justify-center p-2 text-center text-sm text-[#756A6E]'
        )}
      >
        <input
          type="file"
          accept="image/*"
          name="file"
          onChange={handleImageChange}
          ref={fileInputRef}
          className="hidden"
          disabled={disabled}
        />
        <PlusAddIcon size={36} />

        <div className="px-1 pt-4">
          {t('Generate Avatar based on uploaded facial reference image.')}
        </div>

        <div
          className={clsx(
            'absolute inset-0 h-full w-full rounded-lg border border-transparent',
            {
              '!border-[#FFA133]': isDragOver,
            }
          )}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
        ></div>
      </div>
    )

  return (
    <div
      className={clsx(
        'relative flex h-full w-full items-center justify-center'
      )}
    >
      <img
        src={previewUrl || ''}
        className="max-h-full max-w-full object-contain object-center"
        alt={t('Preview')}
      />
      <div className="absolute right-[5px] top-[5px] cursor-pointer">
        {!disabled && <CreateDeleteIcon onClick={handleCancel} />}
      </div>
    </div>
  )
}
